<template>
  <div class="s-table__wrapper">
    <div v-if="$slots.filters || showColumnVisibilityUi" class="s-table__filters">
      <div class="s-table__filtersInner">
        <div v-if="$slots.filters" class="s-table__filtersContent">
          <slot name="filters" />
        </div>
        <div v-if="showColumnVisibilityUi" class="s-table__filtersActions">
          <ColumnVisibilityModal :columns="columnVisibilityPanelColumns" :has-changes-from-default="hasChangesFromDefault" :disabled="currentTableState?.isLoading || isApplying" @confirm="applyColumnVisibility" />
        </div>
      </div>
    </div>
    <ag-grid-vue
      ref="agGridRef"
      class="s-table ag-theme-keelearning"
      :class="{ '-paginationDisabled': disablePagination, '-withFormFields': tableWithFormFields }"
      :suppress-row-click-selection="true"
      :no-rows-overlay-component="props.mightHaveNotAllowedEntries ? EmptyAreaWithNotAlloweEntries : EmptyArea"
      :tooltip-show-delay="100"
      dom-layout="autoHeight"
      :delta-row-data-mode="!tableWithFormFields"
      :suppress-cell-focus="!tableWithFormFields"
      :default-col-def="defaultColumnDefinition"
      :get-row-id="props.getRowId"
      :get-row-class="props.getRowClass"
      :row-class="defaultRowClass"
      row-selection="multiple"
      :animate-rows="false"
      :loading-overlay-component="LoadingOverlay"
      @cell-clicked="onCellClicked"
      @sort-changed="onSortChanged"
      @grid-ready="onGridReady"
      @row-selected="emit('rowSelected', $event)"
      @row-clicked="onRowClicked"
      @first-data-rendered="emit('firstDataRendered', $event)"
    ></ag-grid-vue>
    <pagination v-if="!disablePagination" :store="store" :before-config-change="props.beforeConfigChange" />
  </div>
</template>

<script lang="ts" setup>
import { AgGridVue } from "ag-grid-vue3"
import { computed, onUnmounted, ref, toRef, watch } from "vue"

import "ag-grid-community/styles/ag-grid.css"
import "@/styles/vendor/ag-grid/theme.scss"
import { CellClickedEvent, ColumnState, GridApi, GridReadyEvent, RowClickedEvent, SortChangedEvent } from "ag-grid-community"
import LoadingOverlay from "@/components/KTable/LoadingOverlay.vue"
import { EntryListStore } from "@/store/factories/entryListStore"
import Pagination from "@/components/KTable/Pagination.vue"
import EmptyArea from "@/components/KTable/EmptyArea.vue"
import { storeToRefs } from "pinia"
import EmptyAreaWithNotAlloweEntries from "@/components/KTable/EmptyAreaWithNotAlloweEntries.vue"
import ColumnVisibilityModal from "@/components/KTable/ColumnVisibilityModal.vue"
import { useKTableColumnVisibility, type KTableColumnDefinition, type KTableColumnVisibilityMode, type ReportingColumnConfig } from "@/components/KTable/useKTableColumnVisibility"
import type { ColumnVisibility, ColumnVisibilityContext } from "@/store/columnVisibility"

const props = withDefaults(
  defineProps<{
    store: EntryListStore
    name?: string | null
    getRowId?: (data: any) => any
    getRowClass?: (data: any) => any
    columns: KTableColumnDefinition[]
    clickable?: boolean
    disablePagination?: boolean
    dataKey?: string
    mightHaveNotAllowedEntries?: boolean
    tableWithFormFields?: boolean
    columnVisibilityMode?: KTableColumnVisibilityMode
    columnVisibilityContext?: ColumnVisibilityContext
    reportingColumnConfig?: ReportingColumnConfig | null
    hideColumnVisibility?: boolean
    beforeConfigChange?: (change: { key: "page" | "perPage"; newValue: number; oldValue: number }) => Promise<boolean> | boolean
  }>(),
  {
    name: null,
    getRowId: (data: any) => {
      return data.data.id
    },
    getRowClass: () => {
      return ""
    },
    clickable: false,
    dataKey: "entries",
    disablePagination: false,
    mightHaveNotAllowedEntries: false,
    tableWithFormFields: false,
    columnVisibilityContext: () => ({}),
    columnVisibilityMode: "user_preferences",
    reportingColumnConfig: null,
    hideColumnVisibility: false,
    beforeConfigChange: () => {
      return true
    },
  },
)

const emit = defineEmits<{
  rowClicked: [event: RowClickedEvent]
  rowSelected: [event: any]
  onGridReady: [event: GridReadyEvent]
  firstDataRendered: [event: any]
  "update:reportingColumnConfig": [columnConfig: Record<string, ColumnVisibility>]
}>()

const agGridRef = ref<InstanceType<typeof AgGridVue> | null>(null)
const { currentTableState, isApplying, showColumnVisibilityUi, columnVisibilityPanelColumns, hasChangesFromDefault, visibleGridColumns, getVisibleColumnsForExport, getVisibleColumnKeys, applyColumnVisibility } = useKTableColumnVisibility({
  tableName: computed(() => props.name),
  columns: toRef(props, "columns"),
  mode: toRef(props, "columnVisibilityMode"),
  context: toRef(props, "columnVisibilityContext"),
  reportingColumnConfig: toRef(props, "reportingColumnConfig"),
  hideColumnVisibility: toRef(props, "hideColumnVisibility"),
  clearRows: () => {
    Reflect.set(props.store, "entries", [])
  },
  reloadData: async () => {
    await props.store.reloadData?.()
  },
  updateReportingColumnConfig: (columnConfig: Record<string, ColumnVisibility>) => {
    emit("update:reportingColumnConfig", columnConfig)
  },
})
let gridApi: GridApi | null = null

// ag-grid-vue3 31 batches reactive columnDefs changes across component instances.
// Update each table through its own GridApi without a stale bound columnDefs prop.
watch(
  visibleGridColumns,
  (columns) => {
    gridApi?.setGridOption("columnDefs", columns)
  },
  {
    deep: true,
  },
)

onUnmounted(() => {
  if (!gridApi) {
    return
  }
  if (!gridApi.isDestroyed()) {
    gridApi.destroy()
  }
})

function onGridReady(event: GridReadyEvent) {
  emit("onGridReady", event)
  gridApi = event.api
  gridApi.setGridOption("columnDefs", visibleGridColumns.value)

  watch(
    () => props.store[props.dataKey],
    () => {
      let newData: Array<any> | null = props.store[props.dataKey]
      if (Array.isArray(newData)) {
        newData = [...newData]
      }
      gridApi?.setGridOption("rowData", newData)
      if (!props.tableWithFormFields) {
        gridApi?.redrawRows()
      }
      // Example: a filter request can return [] while loading state stays false.
      // Keep AG Grid overlay in sync with row data, otherwise stale rows can stay visible under "no rows".
      updateOverlay()
    },
    {
      immediate: true,
      deep: true,
    },
  )

  watch(
    () => props.store.isLoading,
    () => {
      updateOverlay()
    },
    {
      immediate: true,
    },
  )
}

function updateOverlay() {
  if (props.store.isLoading) {
    gridApi?.showLoadingOverlay()

    return
  }

  const entries: any[] = props.store[props.dataKey]
  if (!entries?.length) {
    gridApi?.showNoRowsOverlay()

    return
  }

  gridApi?.hideOverlay()
}

function onCellClicked(event: CellClickedEvent) {
  if (typeof event.colDef.checkboxSelection === "boolean" && event.colDef.checkboxSelection) {
    event.node.setSelected(!event.node.isSelected())
    if (event.event) {
      event.event.cancelBubble = true
    }
  }
}

function onRowClicked(event: RowClickedEvent) {
  if (event.event?.cancelBubble) {
    return
  }
  emit("rowClicked", event)
}

function onSortChanged(event: SortChangedEvent) {
  const newSort = {
    by: null as null | string,
    desc: false,
  }
  event.columnApi.getColumnState().forEach((column: ColumnState) => {
    if (column.sort === "asc") {
      newSort.by = column.colId
      newSort.desc = false
    }
    if (column.sort === "desc") {
      newSort.by = column.colId
      newSort.desc = true
    }
  })
  const { sort } = storeToRefs(props.store)
  sort.value.by = newSort.by
  sort.value.desc = newSort.desc
}

const defaultRowClass = computed(() => {
  if (props.clickable) {
    return "-clickableRow"
  }
  return ""
})

const defaultColumnDefinition = computed(() => ({
  comparator() {
    return 0
  },
  resizable: true,
  suppressKeyboardEvent: props.tableWithFormFields,
}))

defineExpose({
  getAPI(): GridApi | null {
    return gridApi
  },
  getVisibleColumnsForExport(): KTableColumnDefinition[] {
    return getVisibleColumnsForExport()
  },
  getVisibleColumnKeys(): string[] {
    return getVisibleColumnKeys()
  },
})
</script>

<style lang="scss" scoped>
.s-table__wrapper {
  background: white;
  border-radius: 4px;
  border: 1px solid $color-grey2;
  filter: drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.05));
}

.s-table__filters {
  padding: 20px 12px 12px 12px;
}

.s-table__filtersInner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.s-table__filtersContent {
  flex: 1;
  min-width: 0;
}

.s-table__filtersActions {
  flex-shrink: 0;
  margin-inline-start: auto;
}

:global(.s-table .-clickableRow:hover) {
  cursor: pointer;
  background-color: $color-grey1;
}

:global(.s-table.-paginationDisabled .ag-row:last-child) {
  border-bottom: none;
}

:global(.s-table.-withFormFields .ag-cell.ag-cell-focus),
:global(.s-table.-withFormFields .ag-cell:focus),
:global(.s-table.-withFormFields .ag-cell:focus-within) {
  border: none;
}
</style>
